import { catchError, switchMap } from 'rxjs/operators';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuthService } from '../services/auth.service';

/**
 * Set on background requests whose failure the caller handles quietly (e.g. take-test's periodic
 * answer save, E2E-13): no error toast. 401 handling still applies.
 */
export const SILENT_ERRORS = new HttpContextToken<boolean>(() => false);

/** Same text as the API's BaseController.GenericErrorMessage. */
const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again later.';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private messageService: MessageService, private authService: AuthService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      // [E2E-35] File downloads use responseType 'blob', so the API's JSON error body (e.g. a 403
      // or 400 ApiResponse) arrives as a Blob. Read it first, so its errorMessage reaches the toast
      // and the caller like any other error.
      catchError((error: HttpErrorResponse) => error.error instanceof Blob
        ? from(this.readBlobError(error)).pipe(switchMap(parsed => throwError(() => parsed)))
        : throwError(() => error)),
      catchError((error: HttpErrorResponse) => {

        // [AUTH-28/44] 401 means the token is expired/invalid (permission denials return 403):
        // end the dead session and land on the login page instead of accumulating error toasts.
        if (error.status === 401) {
          // A 401 may only end the session whose token made the request. If the token this
          // request carried is no longer the tab's active token, that session was already
          // ended (e.g. the second of two parallel 401s from a dead testee session) — logging
          // out again here would cascade into the OTHER store and kill the tester's session.
          const sentToken = request.headers.get('Authorization')?.replace('Bearer ', '');
          if (sentToken && sentToken !== this.authService.storedToken()) {
            return throwError(() => error);
          }

          const hadSession = this.authService.hasStoredSession();
          this.authService.logout();

          if (hadSession) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Session expired',
              detail: 'Please log in again.',
              life: 8000
            });
          }

          return throwError(() => error);
        }

        if (request.context.get(SILENT_ERRORS)) {
          return throwError(() => error);
        }

        // [AUTH-29] errorMessage can be surfaced verbatim: the API guarantees it is either the
        // shared generic message or a deliberate user-facing business message — generic catch
        // blocks never put raw exception text in it (BaseController.GenericErrorMessage, AUTH-30).
        // [E2E-35] Never fall back to error.message: Angular's "Http failure response for <url>: …"
        // exposes the API URL. Responses with no ApiResponse body (e.g. a role-based 403 from
        // [Authorize]) get a fixed message instead.
        const errorMessage = error?.error?.errorMessage
          ?? (error?.error?.text ? "Incorrect serialization - " + error?.error?.text
            : error.status === 0 ? 'API or UI down'
            : error.status === 403 ? "You don't have permission to do that."
            : GENERIC_ERROR_MESSAGE);
        
        function formatErrorMessage(msg: string, maxLen = 30): string {
          const words = msg.split(' ');
          let lines: string[] = [];
          let currentLine = '';

          for (let word of words) {
            while (word.length > maxLen) {
              if (currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = '';
              }
              lines.push(word.slice(0, maxLen));
              word = word.slice(maxLen);
            }
            if ((currentLine + (currentLine ? ' ' : '') + word).length > maxLen) {
              if (currentLine) lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine += (currentLine ? ' ' : '') + word;
            }
          }
          if (currentLine) lines.push(currentLine);
          return lines.join('\n');
        }

        const formattedErrorMessage = formatErrorMessage(errorMessage);

        this.messageService.add({ severity: 'error', summary: 'Error', detail: formattedErrorMessage, life: 20000 });
        console.log(formattedErrorMessage);
        console.error(error);

        return throwError(() => error)
      })
    );
  }

  /** The same error with its Blob body parsed as JSON, or with no body if it isn't JSON. */
  private async readBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    let body: any = null;
    try {
      body = JSON.parse(await (error.error as Blob).text());
    } catch { }

    return new HttpErrorResponse({
      error: body,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url ?? undefined
    });
  }
}

